import { XStack, type XStackProps } from 'tamagui';
import Button from '../Button/Button';

type TabProps = {
  label: string;
  isActive: boolean;
  setActive: () => void;
  loading?: boolean;
};

const TabItem = ({ label, isActive, setActive, loading }: TabProps) => {
  return (
    <Button
      loading={loading}
      onPress={setActive}
      variant="secondary"
      borderColor="$transparent"
      backgroundColor={isActive ? '$accentPale' : '$base'}
      hoverStyle={{
        borderColor: '$transparent',
        backgroundColor: isActive ? '$accentPale' : '$subtlePale',
      }}
      focusStyle={{
        borderColor: '$transparent',
        outlineWidth: 2,
        outlineColor: isActive ? '$accentPale' : '$subtlePale',
        outlineOffset: 2,
        outlineStyle: 'solid',
      }}
      textProps={{ color: isActive ? '$contrast' : '$darkGray' }}
      testID={`tab-${label.toLowerCase()}`}
    >
      {label}
    </Button>
  );
};

type Tab<TTabId> = { id: TTabId; label: string };

type Props<TTabId extends string> = {
  tabs: Array<Tab<TTabId>>;
  activeTabId: TTabId;
  onChange: (item: Tab<TTabId>) => void;
  loading?: boolean;
} & XStackProps;

function Tabs<TTabId extends string>({ tabs, activeTabId, onChange, loading, ...restXStackProps }: Props<TTabId>) {
  return (
    <XStack space="$2" {...restXStackProps}>
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;
        return (
          <TabItem
            key={tab.id}
            loading={loading}
            label={tab.label}
            isActive={isActive}
            setActive={() => onChange(tab)}
          />
        );
      })}
    </XStack>
  );
}

export default Tabs;
